import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GLOBAL_ROLES, TENANT_ADMINISTRATOR } from '@careapp-group/core';
import { Actions, createEffect, Effect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATION } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { CAAction } from '../actions';
import * as actions from '../actions/index';
import { auth, getAuthenticationState } from '../auth/auth.selectors';
import { getSelfCustomer } from '../customers/customers.selectors';
import * as selectors from '../reducers/index';
import { getSelected, ORGANISATIONS } from '../reducers/index';
import { TenantDto } from '@careapp-group/ng-sdk-v2-new';

@Injectable()
/**
 * Effects class for changing routes when an action occurs
 */
export class RouterEffects {
  @Effect({ dispatch: false })
  navigate$ = this.actions$.pipe(
    ofType<CAAction>(actions.GO),
    map((action) => action.payload),
    tap(({ path, query: queryParams, extras }) =>
      this.router.navigate(path, { queryParams, ...extras })
    )
  );

  @Effect()
  organisationCreated = this.actions$.pipe(
    ofType<CAAction>(actions.Organisations.RESPONSE_SUCCESS_CREATE),
    map((action) => actions.NavigateAction({ path: ['/', 'admin', 'organisations'] }))
  );

  tenantInitialRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.Organisations.InitTenant),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.TENANTS)),
        this.store.select(selectors.getIds(selectors.TENANTS)),
        this.store.select(selectors.getEntities(selectors.TENANTS)),
        this.route.queryParams
      ),
      filter(([action, selectedTenant, tenantIds, tenants, routeParams]) => {
        // Selected org no longer exists (or never existed), pick a new one
        let newSelected = (tenantIds as string[]).includes(selectedTenant)
          ? selectedTenant
          : tenantIds[0];
        const isAdminPage = this.router.url.includes('admin');

        // Only act if the selected organisation ID is changing or a route does not exist.
        return !isAdminPage && (newSelected !== selectedTenant || routeParams['tenantId'] == null);
      }),
      map(([action, selectedTenant, tenantIds, tenants, routeParams]) => {
        let newSelected = (tenantIds as string[]).includes(selectedTenant)
          ? selectedTenant
          : tenantIds[0];

        if (tenants[newSelected]?.permission?.role === TENANT_ADMINISTRATOR) {
          return actions.NavigateAction({
            path: ['/', newSelected, 'insights'],
            reason: 'Initial Tenant Navigation',
          });
        }

        if (!!tenantIds.length) {
          return actions.NavigateAction({
            path: ['/', newSelected],
            reason: 'Initial Tenant Navigation',
          });
        } else {
          return actions.NavigateAction({
            path: ['/'],
            reason: 'Navigating to no-provider error screen',
          });
        }
      })
    )
  );

  // Routes to a new organisation feed if necessary.
  organisationInitialRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.Organisations.InitOrganisation),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(ORGANISATIONS)),
        this.store.select(selectors.getIds(ORGANISATIONS)),
        this.route.queryParams,
        this.store.select(selectors.getEntities(ORGANISATIONS)),
        this.store.select(selectors.getSelectedId(selectors.TENANTS)),
        this.store.select(selectors.getIds(selectors.TENANTS))
      ),
      filter(
        ([
          action,
          previousSelectedOrgId,
          organisationIds,
          routeParams,
          orgs,
          previouslySelectedTenantId,
          tenantIds,
        ]) => {
          // Selected tenant no longer exists (or never existed), pick a new one
          let newSelectedTenantId = (tenantIds as string[]).includes(previouslySelectedTenantId)
            ? previouslySelectedTenantId
            : tenantIds[0];

          // Organisations under the new selected tenant.
          const eligibleOrganisationIds = organisationIds.filter(
            (id) => !!orgs[id] && orgs[id].tenantId === newSelectedTenantId
          );

          // Selected org no longer exists (or never existed), pick a new one under the given tenant ID.
          let newSelectedOrgId =
            (organisationIds as string[]).includes(previousSelectedOrgId) &&
            !!orgs[previousSelectedOrgId] &&
            orgs[previousSelectedOrgId].tenantId === newSelectedTenantId
              ? previousSelectedOrgId
              : eligibleOrganisationIds[0];

          const isAdminPage = this.router.url.includes('admin');

          // Only act if the selected organisation ID or tenant is changing or a route does not exist.
          return (
            !isAdminPage &&
            (newSelectedOrgId !== previousSelectedOrgId ||
              !routeParams['organisationId'] ||
              newSelectedTenantId !== previouslySelectedTenantId ||
              !routeParams['tenantId'])
          );
        }
      ),
      map(
        ([
          action,
          previousSelectedOrgId,
          organisationIds,
          routeParams,
          orgs,
          previouslySelectedTenantId,
          tenantIds,
        ]) => {
          let newSelectedTenantId = (tenantIds as string[]).includes(previouslySelectedTenantId)
            ? previouslySelectedTenantId
            : tenantIds[0];

          // Organisations under the new selected tenant.
          const eligibleOrganisationIds = organisationIds.filter(
            (id) => orgs[id] != null && orgs[id].tenantId === newSelectedTenantId
          );

          // Selected org no longer exists (or never existed), pick a new one under the given tenant ID.
          let newSelectedOrgId =
            (organisationIds as string[]).includes(previousSelectedOrgId) &&
            !!orgs[previousSelectedOrgId] &&
            orgs[previousSelectedOrgId].tenantId === newSelectedTenantId
              ? previousSelectedOrgId
              : eligibleOrganisationIds[0];

          return actions.NavigateAction({
            path: ['/', newSelectedTenantId, 'organisations', newSelectedOrgId],
            reason: 'Initial Tenant/Organisation Navigation',
          });
        }
      )
    )
  );

  @Effect()
  tenantCreated = this.actions$.pipe(
    ofType<CAAction>(actions.Tenants.RESPONSE_SUCCESS_CREATE),
    map((action) => actions.NavigateAction({ path: ['/', 'admin', 'tenants'] }))
  );

  @Effect()
  announcementCreated = this.actions$.pipe(
    ofType<CAAction>(actions.Announcements.RESPONSE_SUCCESS_CREATE),
    withLatestFrom(this.store.select(getSelected(ORGANISATIONS))),
    map(([action, org]) =>
      actions.NavigateAction({
        path: ['/', org.tenantId, 'organisations', org.id, 'notices'],
      })
    )
  );

  @Effect()
  announcementUpdated = this.actions$.pipe(
    ofType<CAAction>(actions.Announcements.RESPONSE_SUCCESS_SAVE),
    withLatestFrom(this.store.select(getSelected(ORGANISATIONS))),
    map(([action, org]) =>
      actions.NavigateAction({
        path: ['/', org.tenantId, 'organisations', org.id, 'notices'],
      })
    )
  );

  @Effect({ dispatch: false })
  routerEffects = this.actions$.pipe(
    ofType<CAAction>(ROUTER_NAVIGATION),
    withLatestFrom(
      this.store.select(selectors.isAppAuthorized),
      this.store.select(selectors.getRouterState),
      this.store.select(selectors.getSelected('organisations')),
      this.store.select(auth.isAdmin),
      this.store.select(selectors.org.getSelectable),
      this.store.select(selectors.getAll(selectors.TENANTS))
    ),
    filter(([action, auth, router, selectedOrganisation, admin, orgs, tenants]) => !!auth),
    tap(([action, authorized, router, selectedOrganisation, admin, orgs, tenants]) => {
      if (router.state && !!orgs.length && !!tenants.length) {
        switch (router.state.url) {
          case '/': {
            const tenantAdminTenant = tenants.find(
              (tenant: TenantDto) => tenant.permission?.role === TENANT_ADMINISTRATOR
            );
            if (selectedOrganisation) {
              this.store.dispatch(
                actions.NavigateAction({
                  path: [
                    '/',
                    selectedOrganisation.tenantId,
                    'organisations',
                    selectedOrganisation.id,
                  ],
                })
              );
            } else if (admin) {
              this.store.dispatch(actions.NavigateAction({ path: [`/admin`] }));
            } else if (tenantAdminTenant) {
              this.store.dispatch(
                actions.NavigateAction({
                  path: ['/', orgs[0].tenantId, `insights`],
                })
              );
            } else {
              this.store.dispatch(
                actions.NavigateAction({
                  path: ['/', orgs[0].tenantId, `organisations`, orgs[0].id],
                })
              );
            }
          }
        }
      }
    })
  );

  redirectToLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequireLogin),
      map((action) => actions.NavigateAction({ path: ['/', 'auth'] }))
    )
  );

  redirectToRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequireRegistration),
      map((action) =>
        actions.NavigateAction({
          path: ['/', 'signup'],
          query: action.query,
          extras: {
            replaceUrl: true,
          },
        })
      )
    )
  );

  redirectAfterRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.SuccessCompleteRegistrationFlow),
      withLatestFrom(this.store.select(auth.getPostLoginRoute)),
      map(([action, postLoginRoute]) => {
        if (
          action.response.continue_with?.length > 0 &&
          action.response.continue_with[0].action === 'show_verification_ui'
        ) {
          let query;
          if ((<any>action.response.continue_with[0])?.flow?.url) {
            const url = new URL((<any>action.response.continue_with[0])?.flow?.url);
            query = Object.fromEntries(url.searchParams.entries());
          }

          return actions.NavigateAction({
            path: ['/', 'verification'],
            query: query,
            extras: {
              replaceUrl: true,
            },
          });
        } else if (postLoginRoute) {
          const route = this.router.parseUrl(postLoginRoute);
          return actions.NavigateAction({
            path: route.root.children.primary?.segments?.map((s) => s.path) ?? ['/'],
            query: route.queryParams,
            reason: 'Post login route exists',
            extras: {
              replaceUrl: true,
            },
          });
        }
      })
    )
  );

  @Effect()
  postLoginRedirect = this.actions$.pipe(
    ofType<CAAction>(
      actions.APPLICATION_LOGIN_PROCESS_COMPLETE,
      actions.SuccessUpdateVerificationFlow
    ),
    withLatestFrom(
      this.store.select(getAuthenticationState),
      this.store.select(selectors.getSelected('organisations')),
      this.store.select(selectors.org.getSelectable),
      this.store.select(auth.getPostLoginRoute),
      this.store.select(selectors.getAll(selectors.TENANTS))
    ),
    map(([action, auth, org, orgs, postLoginRoute, tenants]) => {
      org = org || orgs[0];
      const tenantAdminTenant = tenants.find(
        (tenant: TenantDto) => tenant.permission?.role === TENANT_ADMINISTRATOR
      );
      if ((<any>action).response?.return_to) {
        const route = this.router.parseUrl((<any>action).response.return_to);
        return actions.NavigateAction({
          path: route.root.children.primary.segments.map((s) => s.path),
          query: route.queryParams,
          reason: 'action contains a return_to property',
          extras: {
            replaceUrl: true,
          },
        });
      } else if (postLoginRoute) {
        const route = this.router.parseUrl(postLoginRoute);
        return actions.NavigateAction({
          path: route.root.children.primary?.segments?.map((s) => s.path) ?? ['/'],
          query: route.queryParams,
          reason: 'Post login route exists',
          extras: {
            replaceUrl: true,
          },
        });
      } else if (auth.roles && auth.roles.some((r) => GLOBAL_ROLES.includes(r))) {
        return actions.NavigateAction({
          path: ['/admin'],
        });
      } else if (tenantAdminTenant) {
        return actions.NavigateAction({
          path: [tenantAdminTenant.id, 'insights'],
        });
      } else if (org) {
        return actions.NavigateAction({
          path: [org.tenantId, 'organisations', org.id],
        });
      } else {
        console.log('No site information available - falling back to login');
        return actions.NavigateAction({
          path: ['/'],
          extras: {
            replaceUrl: true,
            skipLocationChange: false,
          },
        });
      }
    })
  );

  @Effect()
  customerCreated = this.actions$.pipe(
    ofType<CAAction>(actions.Customers.RESPONSE_SUCCESS_CREATE),
    map((action) => {
      return actions.NavigateAction({
        path: [
          '/',
          action.payload.tenantId,
          'organisations',
          (<any>action).payload.organisationId,
          'communities',
          (<any>action).payload.id,
        ],
      });
    })
  );

  customerFileAdded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.Customers.ResponseSuccessAssignCustomerFile),
      withLatestFrom(
        this.store.pipe(select(getSelected(ORGANISATIONS))),
        this.store.pipe(select(getSelfCustomer))
      ),
      map(([action, organisation, customerSelf]) => {
        if (customerSelf != null) {
          return actions.NavigateAction({
            path: ['/', organisation.tenantId, 'organisations', organisation.id, 'files'],
          });
        } else {
          return actions.NavigateAction({
            path: [
              '/',
              organisation.tenantId,
              'organisations',
              organisation.id,
              'communities',
              action.customerId,
            ],
          });
        }
      })
    )
  );

  customerLinkAdded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.Customers.ResponseSuccessAddLink),
      withLatestFrom(
        this.store.pipe(select(getSelected(ORGANISATIONS))),
        this.store.pipe(select(getSelfCustomer))
      ),
      map(([action, organisation, customerSelf]) => {
        if (customerSelf != null) {
          return actions.NavigateAction({
            path: ['/', organisation.tenantId, 'organisations', organisation.id, 'files'],
          });
        } else {
          return actions.NavigateAction({
            path: [
              '/',
              organisation.tenantId,
              'organisations',
              organisation.id,
              'communities',
              action.customer.id,
            ],
          });
        }
      })
    )
  );

  customerListCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CustomerLists.RESPONSE_SUCCESS_CREATE),
      withLatestFrom(this.store.pipe(select(getSelected(ORGANISATIONS)))),
      map(([action, organisation]) => {
        return actions.NavigateAction({
          path: [
            '/',
            organisation.tenantId,
            'organisations',
            organisation.id,
            'customer-lists',
            action.payload.id,
          ],
        });
      })
    )
  );

  customerListRemoved$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CustomerLists.RESPONSE_SUCCESS_DESTROY_BY_ID),
      withLatestFrom(this.store.pipe(select(getSelected(ORGANISATIONS)))),
      map(([action, organisation]) => {
        return actions.NavigateAction({
          path: ['/', organisation.tenantId, 'organisations', organisation.id, 'communities'],
        });
      })
    )
  );

  @Effect()
  navigateAfterLogout = this.actions$.pipe(
    ofType(actions.ResponseSuccessAuthLogoutAction, actions.RESPONSE_FAIL_AUTH_LOGOUT),
    filter((action) => action.stayOnCurrentRoute !== true),
    map((action) =>
      actions.NavigateAction({
        path: ['/', 'auth'],
        extras: {
          replaceUrl: true,
          skipLocationChange: false,
        },
      })
    )
  );

  @Effect()
  feedbackPosted = this.actions$.pipe(
    ofType(actions.Feedback.ResponseSuccessCreateAction),
    withLatestFrom(this.store.select(getSelected(ORGANISATIONS))),
    map(([action, org]) =>
      actions.NavigateAction({
        path: ['/', org.tenantId, 'organisations', org.id],
      })
    )
  );

  @Effect()
  conversationSelected = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.Conversations.responseSuccessSelectConversation),
      map((action) =>
        actions.NavigateAction({
          query: {
            cid: action.cid,
          },
          path: ['/', 'conversations'],
        })
      )
    )
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private router: Router,
    private route: ActivatedRoute
  ) {}
}
